/* The symbol's 1-minute bars, live, for a reader that needs them while the chart shows another timeframe (the entry
   marks' 5m / 15m / 4H / day ranges, components/EntryMarksLayer.tsx). The chart's own recipe on the 1-minute series (lib/forming.ts):
   the closed bars from the hydrate, the screener's forming frames and the trade prints stitched by LiveCandles.
   Subscribed before the hydrate, buffered until it lands (ChartPane's order), so nothing between them is lost.
   Off (`enabled` false — the chart is on 1m and its own bars serve) → no request, no subscription, an empty array.
   The array is mutated in place; read `.current` when drawing. */
import React from 'react';
import { LiveCandles, type Candle, type FormingSnap, type Print } from './forming';
import { MOCK } from './mode';
import { getCandles, getForming, subscribeForming, subscribeTrades } from '../data/source';

/** the readers need the whole session (the day bar, 04:00–20:00 ET = 960 bars) and the last 4 hours: keep a session's
 *  worth, cut back now and then */
const KEEP = 1000, TRIM_AT = 1200;

export function useMinuteBars(symbol: string, enabled: boolean): React.MutableRefObject<Candle[]> {
  const ref = React.useRef<Candle[]>([]);
  React.useEffect(() => {
    ref.current = [];
    if (!enabled || !symbol) return;
    let alive = true;
    let lc: LiveCandles | null = null;
    const prints: Print[] = [], snaps: FormingSnap[] = [];
    const pick = (f: any): FormingSnap => ({
      asOf: f && typeof f.asOf === 'number' ? f.asOf : undefined,
      wm: f && f.wm ? f.wm : null,
      forming: f && f.forming ? (f.forming['1Min'] ?? null) : null,
      lastClosed: f && f.lastClosed ? (f.lastClosed['1Min'] ?? null) : null,
    });
    const trim = () => { const a = ref.current; if (a.length > TRIM_AT) a.splice(0, a.length - KEEP); };
    const offTrades = MOCK ? () => {} : subscribeTrades(symbol, (t: any) => {
      const p: Print = { ts: t.timestamp ? new Date(t.timestamp).getTime() : Date.now(), price: Number(t.price), size: Number(t.size) || 0 };
      if (lc) { try { lc.applyPrint(p); } catch (e) { /* one bad print ≠ dead bars */ } }
      else if (prints.push(p) > 50_000) prints.splice(0, 10_000);
    });
    const offForming = MOCK ? () => {} : subscribeForming(symbol, (f: any) => {
      const sn = pick(f);
      if (lc) { try { lc.applySnapshot(sn); trim(); } catch (e) { /* the next frame rebuilds it */ } }
      else snaps.push(sn);
    });
    Promise.all([getCandles(symbol, '1'), MOCK ? Promise.resolve(null) : getForming(symbol, '1')]).then(([candles, fs]: any[]) => {
      if (!alive) return;
      const arr: Candle[] = (candles || []).slice(-KEEP).map((d: any) => ({ time: d.time, open: d.open, high: d.high, low: d.low, close: d.close, value: d.value || 0, up: d.close >= d.open }));
      ref.current = arr;
      if (MOCK) return;
      lc = new LiveCandles(arr, 1);
      try {
        if (fs) lc.applySnapshot({ asOf: fs.asOf, wm: fs.wm ?? null, forming: fs.forming ?? null, lastClosed: fs.lastClosed ?? null });
        for (const p of prints.splice(0)) lc.applyPrint(p);
        for (const sn of snaps.splice(0)) lc.applySnapshot(sn);
      } catch (e) { /* the next frame rebuilds it */ }
      trim();
    }).catch(() => { /* no bars: the marks stay off until the chart's own feed or a later symbol */ });
    return () => { alive = false; offTrades(); offForming(); };
  }, [symbol, enabled]);
  return ref;
}
